/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { SubmitHandler, useForm } from "react-hook-form";
import {
  FlexContainer,
  Input,
  Label,
  Message,
  Spacer,
  Text,
  theme,
} from "@nordcloud/gnui";
import { ValidationError } from "~/generated/graphql";
import { ExternalLink, FormGroup, FormWrap } from "~/components";
import { isNotEmpty } from "~/tools";
import { StepContainer } from "~/views/accounts/AddAccount/components/StepContainer";
import { validateAzureJwt } from "~/views/accounts/validators";
import {
  azureEnrollmentAccessKeyGuideLink,
  azureEnrollmentNumberGuideLink,
} from "~/views/cloudAccounts/BillingData/constants";

enum FormField {
  ENROLLMENT_NUMBER = "enrollment_number",
  ENROLLMENT_ACCESS_KEY = "enrollment_access_key",
}

type FormData = {
  [FormField.ENROLLMENT_NUMBER]: string;
  [FormField.ENROLLMENT_ACCESS_KEY]: string;
};

type Props = {
  onSubmit: SubmitHandler<FormData>;
  onPrevStep: () => void;
  validationErrors?: ValidationError[];
};

export function AzureEAForm({
  onSubmit,
  onPrevStep,
  validationErrors = [],
}: Props) {
  const {
    register,
    handleSubmit,

    formState: { isValid, errors },
  } = useForm<FormData>({ mode: "onChange" });

  const shouldDisableSubmit = !isValid || isNotEmpty(Object.keys(errors));

  return (
    <FormWrap
      footerProps={{
        nextButtonProps: { disabled: shouldDisableSubmit },
        onPrevClick: onPrevStep,
      }}
      onSubmit={handleSubmit(onSubmit)}
    >
      <FlexContainer wrap="wrap" direction="column" alignItems="stretch">
        <Text weight="medium">Account Credentials</Text>
        <Text size="md" color={theme.color.text.text02}>
          To on-board a subscription you need to create a Service Principal.
        </Text>
        <Spacer height={theme.spacing.spacing02} />
        <StepContainer counter={1}>
          <Text>
            Follow this guide to obtain your enrollment number
            <br />
            <ExternalLink href={azureEnrollmentNumberGuideLink}>
              Azure: Manage your enrollment
            </ExternalLink>
          </Text>
        </StepContainer>
        <StepContainer counter={2}>
          <Text>
            Follow this guide to obtain your enrollment access key
            <br />
            <ExternalLink href={azureEnrollmentAccessKeyGuideLink}>
              Azure api key generation
            </ExternalLink>
          </Text>
        </StepContainer>
        <StepContainer counter={3}>
          <Text>Add this information to access your billing data:</Text>
          {validationErrors
            .filter(({ field }) => field === null || field === "")
            .map(({ error }) => (
              <Message key={error} status="danger" image="danger">
                {error}
              </Message>
            ))}
          <Spacer height={theme.spacing.spacing04} />
          <div css={{ width: "100%" }}>
            <FormGroup error={errors[FormField.ENROLLMENT_NUMBER]}>
              <Label
                required
                htmlFor={FormField.ENROLLMENT_NUMBER}
                name="Enrollment number"
              />
              <Input
                id={FormField.ENROLLMENT_NUMBER}
                {...register(FormField.ENROLLMENT_NUMBER, {
                  required: "This field is required.",
                  pattern: {
                    value: /\d{7,8}/,
                    message: "Enter a 7 or 8-digit number.",
                  },
                  minLength: {
                    value: 7,
                    message: "Minimum 7 characters required.",
                  },
                  maxLength: {
                    value: 8,
                    message: "Maximum 8 characters allowed.",
                  },
                })}
              />
            </FormGroup>
            <FormGroup error={errors[FormField.ENROLLMENT_ACCESS_KEY]}>
              <Label
                required
                htmlFor={FormField.ENROLLMENT_ACCESS_KEY}
                name="Enrollment Access Key"
              />
              <Input
                id={FormField.ENROLLMENT_ACCESS_KEY}
                {...register(FormField.ENROLLMENT_ACCESS_KEY, {
                  required: "This field is required.",
                  validate: validateAzureJwt,
                })}
                placeholder="e.g., eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c"
              />
            </FormGroup>
          </div>
        </StepContainer>
      </FlexContainer>
    </FormWrap>
  );
}
